import React from 'react';
import { Box, Button, Dialog, DialogFooter, Flex, Text } from 'core/components';
import DialogBody from 'core/components/DialogBody';
import { Field, InputGroup, Switch } from 'core/form';
import { observer } from 'mobx-react';
import { Radio } from '@blueprintjs/core';
import { OptionFieldState, OptionValue, VariableFieldState } from 'app/stores/ude/variables.types';

interface Props {
  isOpen: boolean;
  isNew: boolean;
  onClose: () => void;
  onSave: () => void;
  variable: VariableFieldState | null;
}

function BoardVariableDialog({ isOpen, isNew, onClose, onSave, variable }: Props) {
  const handleSave = () => {
    // remove empty options
    if (variable) {
      const options: OptionValue[] = variable.options.getValue();
      const filtered = options.filter((option) => option.value || option.label);
      variable.options.setValue(filtered);

      // if there's exactly one option, auto-set it as the default
      if (filtered.length === 1 && filtered[0].value) {
        variable.value.onChange(filtered[0].value);
      }
    }
    onSave();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={`${isNew ? 'Add' : 'Edit'} Custom Filter`}
      width={900}
      my={100}
      maxHeight="calc(100vh - 200px)"
      alignSelf="flex-start"
    >
      {variable && (
        <DialogBody p={0}>
          <Box p={2}>
            <Flex alignItems="center" gap={1}>
              <Field large field={variable.name} flex={1} maxWidth={450}>
                <InputGroup />
              </Field>
            </Flex>
            <Flex flexDirection="column" my={1}>
              <Text small>Each row will appear as an option in a dropdown list</Text>
              <Box my="4px" border="thin" borderRadius={8} bg="cardBackground" p="4px">
                {variable.options.size() === 0 && (
                  <Flex p={2} justifyContent="center">
                    <Text small muted>
                      No options defined
                    </Text>
                  </Flex>
                )}
                {variable.options.size() > 0 && (
                  <Flex gap={1} px={1} pt={1} pb={0}>
                    <Text small fontWeight="medium" flex={1}>
                      Label
                    </Text>
                    <Text small fontWeight="medium" flex={1}>
                      Value
                    </Text>
                    <Text small fontWeight="medium" style={{ width: 28, textAlign: 'center' }}>
                      Default
                    </Text>
                    <Box width={24}>&nbsp;</Box>
                  </Flex>
                )}
                {variable.options.map((option: OptionFieldState, optIdx: number) => {
                  const isOnlyOption = variable.options.size() === 1;
                  const isDefault =
                    isOnlyOption || (!!option.value.value && option.value.value === variable.value.value);
                  return (
                    <Flex
                      key={option.value._id}
                      gap={1}
                      alignItems="center"
                      borderTop={optIdx > 0 ? 'thin' : undefined}
                      p={1}
                    >
                      <Field field={option.label} placeholder={option.value.value} showLabel={false} m={0} flex={1}>
                        <InputGroup />
                      </Field>
                      <Field field={option.value} showLabel={false} m={0} flex={1}>
                        <InputGroup />
                      </Field>
                      <Radio
                        checked={isDefault}
                        onChange={() => variable.value.onChange(option.value.value)}
                        style={{ marginBottom: 0, display: 'flex', justifyContent: 'center', width: 28 }}
                      />
                      <Button
                        icon="cross"
                        title="Remove option"
                        onClick={() => variable.options.remove(optIdx)}
                        disabled={variable.options.size() <= 1}
                        intent="danger"
                        minimal
                        small
                      />
                    </Flex>
                  );
                })}
              </Box>
              <Button
                icon="plus"
                text="Add Dropdown Option"
                small
                minimal
                onClick={() => variable.options.add({ label: '', value: '' })}
                alignSelf="flex-end"
              />
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Field field={variable.allowMulti} m={0} showLabel={false} large>
                <Switch switchLabel="Multiple selections" />
              </Field>
              <Field field={variable.exactMatch} m={0} showLabel={false} large>
                <Switch switchLabel="Force exact match" />
              </Field>
            </Flex>
          </Box>
        </DialogBody>
      )}
      <DialogFooter gap={1}>
        <Button text="Cancel" onClick={onClose} />
        <Button text="Save" onClick={handleSave} intent="primary" />
      </DialogFooter>
    </Dialog>
  );
}

export default observer(BoardVariableDialog);
